import React, { useEffect } from 'react';
import ReactGA from 'react-ga4';
import { useNavigate } from 'react-router-dom';
import { number, object, string } from 'yup';

import { useAlerta } from 'Component/Alerta';
import { AnimacaoCartaoDeCredito } from 'Component/Animacao';
import Botao from 'Component/Botao';
import Form, { Campo, Selecao } from 'Component/Form';
import Imagem from 'Component/Imagem';
import Assinatura from 'Interface/Assinaturas';
import Plano from 'Interface/Planos';
import { Erro } from 'Interface/Requisicao';
import { CartaoDeCredito } from 'Interface/Transacoes';
import {
  useAdicionarAssinaturaMutation,
  useEditarAssinaturaMutation,
  useRecuperarAssinaturasQuery,
  useRecuperarPlanosQuery,
} from 'Service';
import { BANDEIRAS, MASCARA_DATA_VENCIMENTO } from 'View/constants';
import { errosParaLista } from 'View/functions';

const regras = object({
  nome: string()
    .required('Obrigatório')
    .max(26, 'O nome deve ser igual ao que consta em seu cartão'),
  numero: number().required('Obrigatório'),
  vencimento: string().required('Obrigatório'),
  codigo: number().required('Obrigatório'),
  bandeira: string().required('Obrigatório'),
});

const Adicionar = (): React.ReactElement => {
  const alerta = useAlerta();
  const navegacao = useNavigate();
  const [adicionarAssinatura] = useAdicionarAssinaturaMutation();
  const [editarAssinatura] = useEditarAssinaturaMutation();

  const {
    data: { assinaturas: [assinatura] } = { assinaturas: [{} as Assinatura] },
    refetch,
  } = useRecuperarAssinaturasQuery();
  const { data: { planos: [plano] } = { planos: [{} as Plano] } } =
    useRecuperarPlanosQuery({ limit: 1 });

  useEffect(() => {
    refetch();
  }, [refetch]);

  const enviar = async (dados: CartaoDeCredito): Promise<void> => {
    try {
      if (!assinatura) {
        await adicionarAssinatura({
          plano_id: plano.id,
          cartao: { ...dados },
        }).unwrap();
      } else {
        await editarAssinatura({
          id: assinatura.id,
          plano_id: plano.id,
          status: 1,
          cartao: { ...dados },
        }).unwrap();
      }

      ReactGA.event('add_payment_info', {
        currency: 'BRL',
        value: plano.valor,
        payment_type: 'Cartão de crédito',
        items: [
          {
            item_id: plano.id,
            item_name: plano.nome,
            price: plano.valor,
          },
        ],
      });
      ReactGA.event('purchase', {
        currency: 'BRL',
        value: plano.valor,
        items: [
          {
            item_id: plano.id,
            item_name: plano.nome,
            price: plano.valor,
          },
        ],
      });

      navegacao('/estudar/minha-assinatura');
    } catch (erros) {
      alerta({
        titulo: 'Erro ao realizar o cadastro da assinatura',
        conteudo: errosParaLista(erros as Erro),
        tema: 'danger',
      });
    }
  };

  return (
    <div className="animate__animated animate__fadeIn mh-100">
      <div
        className="container-fluid mh-100"
        style={{
          backgroundImage: 'url(/img/fundo-assinar.webp)',
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'right',
          marginTop: '-130px',
          paddingTop: '130px',
        }}
      >
        <div className="row mh-100 justify-content-center align-items-end px-3 px-sm-0">
          <div className="col-xl-4 col-xxl-3 mt-5 mt-sm-0">
            <h1 className="fw-lighter text-white mb-3 text-center text-sm-end">
              Quase lá....
            </h1>
            {assinatura ? (
              <>
                <p className="lead text-muted text-center text-sm-end">
                  Para que seu cadastro seja validado e sua assinatura seja
                  efetivada, é necessário informar seus dados de cobrança.
                </p>
                <p className="lead text-muted text-center text-sm-end">
                  Os dados informados ficam armazenados com nosso parceiro{' '}
                  <span className="text-danger fw-bold">Santander</span>, via
                  GetNet. Será debitado em seu cartão o valor de{' '}
                  <span className="text-secondary fw-bold">
                    {plano.valor?.moeda()}
                  </span>{' '}
                  a cada 30 dias a partir da data de hoje, sem uso do limite.
                </p>
              </>
            ) : (
              <>
                <p className="lead text-muted text-center text-sm-end">
                  Para que seu cadastro seja validado, é necessário informar
                  seus dados de cobrança. Esses dados são usados somente para
                  validar seu cadastro, pois{' '}
                  <span className="text-primary fw-bold">NENHUMA cobrança</span>{' '}
                  ocorrerá nos primeiros{' '}
                  <span className="text-secondary fw-bold">7 dias.</span>
                </p>
                <p className="lead text-muted text-center text-sm-end">
                  Os dados informados ficam armazenados com nosso parceiro{' '}
                  <span className="text-danger fw-bold">Santander</span>, via
                  GetNet. Após os 7 Dias Grátis será debitado em seu cartão o
                  valor de{' '}
                  <span className="text-secondary fw-bold">
                    {plano.valor?.moeda()}
                  </span>{' '}
                  a cada 30 dias, sem uso do limite.
                </p>
              </>
            )}
            <small className="d-block text-muted text-center text-sm-end">
              *Lembramos que você pode cancelar sua assinatura a qualquer
              momento e sem custo.
            </small>
            <div className="d-flex justify-content-center justify-content-sm-end mt-3">
              <Imagem
                src="santander.webp"
                alt="Pague com segurança: Santander"
                className="rounded"
                height={50}
              />
            </div>
          </div>
          <div className="col-xl-4 col-xxl-3 mb-5 mb-sm-0">
            <AnimacaoCartaoDeCredito />
            <div className="rounded bg-white p-3">
              <Form onSubmit={enviar} regras={regras}>
                <Selecao
                  name="bandeira"
                  rotulo="Bandeira do cartão"
                  opcoes={BANDEIRAS}
                />
                <Campo name="nome" rotulo="Nome como consta no cartão" />
                <Campo name="numero" rotulo="Número do cartão" />
                <div className="row">
                  <div className="col-6">
                    <Campo
                      name="vencimento"
                      rotulo="Dt. vencimento"
                      mascara={MASCARA_DATA_VENCIMENTO}
                      semMargem
                    />
                  </div>
                  <div className="col-6">
                    <Campo name="codigo" rotulo="Cód. segurança" semMargem />
                  </div>
                </div>
                <div className="mt-3 d-flex">
                  <Botao lg type="submit" className="w-100">
                    Adquirir minha Assinatura
                  </Botao>
                </div>
              </Form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Adicionar;
